import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Package, Loader2, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * OrdersList — /transactions/orders
 * Compact list of my orders as buyer or provider.
 */
const STATE_COLOR = {
  AGREED:              "bg-purple-100 text-purple-800",
  ORDERED:             "bg-blue-100 text-blue-800",
  TRANSPORT_REQUIRED:  "bg-amber-100 text-amber-800",
  TRANSPORT_ASSIGNED:  "bg-cyan-100 text-cyan-800",
  PICKUP:              "bg-cyan-100 text-cyan-800",
  IN_TRANSIT:          "bg-cyan-100 text-cyan-800",
  DELIVERED:           "bg-emerald-100 text-emerald-800",
  COMPLETED:           "bg-emerald-600 text-white",
  CANCELLED:           "bg-stone-300 text-stone-700",
};

export default function OrdersList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/transactions/orders");
      setItems(r.data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Please log in."); nav("/"); return; }
      toast.error("Couldn't load orders");
    } finally { setBusy(false); }
  }, [nav]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 pb-20" data-testid="txn-orders-list">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center gap-2">
          <button onClick={() => nav(-1)} className="p-2 -ml-1 rounded-full hover:bg-stone-100">
            <ArrowLeft size={18}/>
          </button>
          <Package size={16} className="text-blue-600"/>
          <div className="font-black text-stone-900">My Orders</div>
          <button
            onClick={() => nav("/transactions/inbox")}
            data-testid="orders-inbox-link"
            className="ml-auto text-[11px] font-black text-emerald-700 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50">
            <Inbox size={12}/> Inbox
          </button>
          <div className="text-[11px] text-stone-500 font-bold">
            {items.length}
          </div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto p-3 space-y-2">
        {busy && items.length === 0 && (
          <div className="py-16 text-center text-stone-500">
            <Loader2 size={20} className="animate-spin inline-block mr-1.5"/> Loading…
          </div>
        )}
        {!busy && items.length === 0 && (
          <div className="py-16 text-center text-stone-500 text-sm" data-testid="txn-orders-empty">
            No orders yet. Post a Need in Live World to start.
          </div>
        )}
        {items.map((o) => (
          <button key={o.order_id}
                  onClick={() => nav(`/transactions/order/${o.order_id}`)}
                  className="w-full text-left bg-white border border-stone-200 rounded-2xl p-3"
                  data-testid={`txn-order-${o.order_id}`}>
            <div className="flex items-center justify-between">
              <div className="font-black text-stone-900 text-sm truncate">{o.product}</div>
              <div className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${STATE_COLOR[o.state] || "bg-stone-200"}`}>
                {(o.state || "").replace("_", " ")}
              </div>
            </div>
            <div className="text-xs text-stone-500 font-bold mt-0.5">
              {o.quantity} {o.unit} · ₹{Number(o.subtotal || 0).toLocaleString("en-IN")}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
